/**
 * cart-limit.js – Ketomocni v2
 * Blokada: max 1 sztuka tego samego produktu w koszyku.
 * Obsługuje race conditions, Shopify Bundles (has_components), szybkie kliknięcia.
 */

(function () {
  'use strict';

  // ─── MUTEX – blokada równoległych żądań ──────────────────────────────────────
  let isAdding = false;

  // ─── TOAST ───────────────────────────────────────────────────────────────────

  const TOAST_STYLES = `
    #km-cart-toast {
      position: fixed;
      top: 20px;
      right: 20px;
      z-index: 99999;
      background: #1a3a1a;
      color: #f5f0e8;
      border-left: 4px solid #c9a84c;
      border-radius: 6px;
      padding: 14px 18px;
      max-width: 320px;
      box-shadow: 0 4px 20px rgba(0,0,0,0.35);
      font-family: inherit;
      font-size: 14px;
      line-height: 1.5;
      display: none;
    }
    #km-cart-toast.km-visible {
      display: block;
      animation: kmSlideIn 0.3s ease;
    }
    @keyframes kmSlideIn {
      from { opacity: 0; transform: translateX(30px); }
      to   { opacity: 1; transform: translateX(0); }
    }
    #km-cart-toast a {
      display: inline-block;
      margin-top: 8px;
      color: #c9a84c;
      text-decoration: underline;
      font-weight: 600;
    }
    #km-cart-toast-close {
      float: right;
      margin-left: 10px;
      cursor: pointer;
      color: #c9a84c;
      font-size: 18px;
      line-height: 1;
    }
  `;

  function injectStyles() {
    if (document.getElementById('km-cart-toast-styles')) return;
    const s = document.createElement('style');
    s.id = 'km-cart-toast-styles';
    s.textContent = TOAST_STYLES;
    document.head.appendChild(s);
  }

  function createToast() {
    if (document.getElementById('km-cart-toast')) return;
    const t = document.createElement('div');
    t.id = 'km-cart-toast';
    t.innerHTML = `
      <span id="km-cart-toast-close" aria-label="Zamknij">×</span>
      <strong>Ten produkt jest już w Twoim koszyku.</strong><br>
      <a href="/cart">Przejdź do koszyka</a>
    `;
    document.body.appendChild(t);
    document.getElementById('km-cart-toast-close').addEventListener('click', hideToast);
  }

  let toastTimer = null;
  function showToast() {
    const t = document.getElementById('km-cart-toast');
    if (!t) return;
    t.classList.add('km-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, 5000);
  }
  function hideToast() {
    const t = document.getElementById('km-cart-toast');
    if (t) t.classList.remove('km-visible');
  }

  // ─── CART API ─────────────────────────────────────────────────────────────────

  async function fetchCart() {
    const res = await fetch('/cart.js', {
      headers: { 'Content-Type': 'application/json' },
      // Pomijamy intercept – bezpośrednie wywołanie oryginalnego fetch
    });
    return res.json();
  }

  // Sprawdza czy produkt (po variant_id LUB product_id) jest już w koszyku
  async function isAlreadyInCart(variantId, productId) {
    const cart = await fetchCart();
    const items = cart.items || [];
    for (const item of items) {
      if (variantId && String(item.variant_id) === String(variantId)) return true;
      if (productId && String(item.product_id) === String(productId)) return true;
    }
    return false;
  }

  // Pobiera product_id dla danego variant_id przez Storefront
  async function getProductIdForVariant(variantId) {
    try {
      const res = await _originalFetch(`/variants/${variantId}.js`);
      const data = await res.json();
      return data.product_id || null;
    } catch (e) {
      return null;
    }
  }

  // ─── ZABEZPIECZENIE AWARYJNE – quantity > 1 ──────────────────────────────────

  async function enforceMaxQuantity() {
    try {
      const cart = await fetchCart();
      for (const item of cart.items || []) {
        if (item.quantity > 1) {
          await _originalFetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: item.key, quantity: 1 }),
          });
        }
      }
    } catch (e) {
      console.warn('[KM] enforceMaxQuantity error:', e);
    }
  }

  // ─── INTERCEPT FETCH ──────────────────────────────────────────────────────────

  const _originalFetch = window.fetch.bind(window);

  window.fetch = async function (input, init) {
    const url = (typeof input === 'string' ? input : (input && input.url)) || '';

    if (/\/cart\/add(\.js)?(\?|$)/.test(url)) {

      // Mutex – blokuj równoległe żądania
      if (isAdding) {
        showToast();
        return new Response(JSON.stringify({ status: 422, message: 'Duplicate request blocked' }), {
          status: 422,
          headers: { 'Content-Type': 'application/json' },
        });
      }

      let body = null;
      try {
        if (init && init.body) {
          body = typeof init.body === 'string' ? JSON.parse(init.body) : init.body;
        }
      } catch (e) {}

      const variantId = body && (body.id || body.variant_id);

      // Pobierz product_id dla tego wariantu
      let productId = body && body.product_id;
      if (!productId && variantId) {
        productId = await getProductIdForVariant(variantId);
      }

      const inCart = await isAlreadyInCart(variantId, productId);
      if (inCart) {
        showToast();
        return new Response(JSON.stringify({ status: 422, message: 'Already in cart' }), {
          status: 422,
          headers: { 'Content-Type': 'application/json' },
        });
      }

      isAdding = true;
      try {
        const result = await _originalFetch(input, init);
        setTimeout(enforceMaxQuantity, 500);
        return result;
      } finally {
        // Zwolnij mutex po krótkim czasie (zapobiega race condition przy animacjach)
        setTimeout(() => { isAdding = false; }, 1000);
      }
    }

    return _originalFetch(input, init);
  };

  // ─── INTERCEPT XMLHttpRequest ─────────────────────────────────────────────────

  const _XHROpen = XMLHttpRequest.prototype.open;
  const _XHRSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url) {
    this._km_url = url || '';
    return _XHROpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function (body) {
    if (this._km_url && /\/cart\/add(\.js)?(\?|$)/.test(this._km_url)) {

      if (isAdding) {
        showToast();
        return;
      }

      let variantId = null;
      let productId = null;

      try {
        const parsed = JSON.parse(body);
        variantId = parsed.id || parsed.variant_id || null;
        productId = parsed.product_id || null;
      } catch (e) {
        try {
          const params = new URLSearchParams(body);
          variantId = params.get('id') || params.get('variant_id');
        } catch (e2) {}
      }

      const xhrInstance = this;

      const proceed = async () => {
        if (!productId && variantId) {
          productId = await getProductIdForVariant(variantId);
        }
        const inCart = await isAlreadyInCart(variantId, productId);
        if (inCart) {
          showToast();
          return;
        }
        isAdding = true;
        _XHRSend.call(xhrInstance, body);
        xhrInstance.addEventListener('load', () => {
          setTimeout(enforceMaxQuantity, 500);
          setTimeout(() => { isAdding = false; }, 1000);
        });
      };

      proceed();
      return;
    }
    return _XHRSend.apply(this, arguments);
  };

  // ─── INTERCEPT FORM SUBMIT ────────────────────────────────────────────────────

  document.addEventListener('submit', async function (e) {
    const form = e.target;
    if (!form || !form.action || !form.action.includes('/cart/add')) return;

    if (isAdding) {
      e.preventDefault();
      e.stopImmediatePropagation();
      showToast();
      return;
    }

    const formData = new FormData(form);
    const variantId = formData.get('id') || formData.get('variant_id');

    if (!variantId) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    const productId = await getProductIdForVariant(variantId);
    const inCart = await isAlreadyInCart(variantId, productId);

    if (inCart) {
      showToast();
      return;
    }

    // Nie ma w koszyku — wyślij formularz normalnie
    isAdding = true;
    form.submit();
    setTimeout(() => { isAdding = false; }, 1500);

  }, true);

  // ─── INIT ─────────────────────────────────────────────────────────────────────

  function init() {
    injectStyles();
    createToast();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
